import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import CTAButton from '@/components/shared/CTAButton'
import SEO from '@/components/shared/SEO'

const MarkdownPage = () => {
  const { page } = useParams()
  const prerenderContent = globalThis.__YCM_PRERENDER_DATA__?.markdown || ''
  const [content, setContent] = useState(prerenderContent)
  const [loading, setLoading] = useState(!prerenderContent)
  const [error, setError] = useState(null)

  const pageConfig = useMemo(
    () => ({
      'terms-and-conditions': {
        title: 'Terms and Conditions',
        file: '/content/terms-and-conditions.md',
      },
      'privacy-policy': {
        title: 'Privacy Policy',
        file: '/content/privacy-policy.md',
      },
      'cookie-policy': {
        title: 'Cookie Policy',
        file: '/content/cookie-policy.md',
      },
      'terms-of-use': {
        title: 'Terms of Use',
        file: '/content/terms-of-use.md',
      },
      'complaints-procedure': {
        title: 'Complaints Procedure',
        file: '/content/complaints-procedure.md',
      },
      'mlm-information-and-key-facts': {
        title: 'Information & Key Facts',
        file: '/content/mlm-key-facts.md',
      },
      faqs: {
        title: 'Frequently Asked Questions',
        file: '/content/faqs.md',
        hasHero: true,
        heroTitle: 'Frequently Asked Questions',
        heroSubtitle:
          "Get answers to the most common questions about car finance mis-selling claims. Find out if you're eligible for compensation and learn about the claims process.",
      },
    }),
    []
  )

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        setLoading(true)
        const config = pageConfig[page]

        if (!config) {
          throw new Error('Page not found')
        }

        const response = await fetch(config.file)
        if (!response.ok) {
          throw new Error('Failed to load content')
        }

        const text = await response.text()
        setContent(text)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (page) {
      loadMarkdown()
    }
  }, [page, pageConfig])

  const renderMarkdown = markdown => {
    // Normalize bullet character to dash for consistent rendering
    const normalizedMarkdown = markdown.replace(/^• /gm, '- ')
    const lines = normalizedMarkdown.split('\n')
    const elements = []
    let currentIndex = 0

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]

      // Skip empty lines
      if (line.trim() === '') {
        continue
      }

      // Handle headings
      if (line.startsWith('### ')) {
        const processedHeading = line
          .replace('### ', '')
          .replace(
            /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
            '<span class="font-raleway-numbers">$1</span>'
          )
        elements.push(
          <h3
            key={currentIndex++}
            className="text-foreground mt-6 mb-3 text-2xl font-semibold"
            dangerouslySetInnerHTML={{ __html: processedHeading }}
          ></h3>
        )
      } else if (line.startsWith('## ')) {
        const processedHeading = line
          .replace('## ', '')
          .replace(
            /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
            '<span class="font-raleway-numbers">$1</span>'
          )
        elements.push(
          <h2
            key={currentIndex++}
            className="text-foreground mt-8 mb-4 text-3xl font-semibold"
            dangerouslySetInnerHTML={{ __html: processedHeading }}
          ></h2>
        )
      } else if (line.startsWith('# ')) {
        const processedHeading = line
          .replace('# ', '')
          .replace(
            /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
            '<span class="font-raleway-numbers">$1</span>'
          )
        elements.push(
          <h1
            key={currentIndex++}
            className="text-foreground mb-6 text-4xl font-bold"
            dangerouslySetInnerHTML={{ __html: processedHeading }}
          ></h1>
        )
      }
      // Handle tables
      else if (line.startsWith('|')) {
        const tableRows = []
        let j = i
        while (j < lines.length && lines[j].startsWith('|')) {
          tableRows.push(lines[j])
          j++
        }

        if (tableRows.length > 0) {
          // Check if this is the fees table (has specific structure)
          const isFeesTable = tableRows.some(row =>
            row.includes('Consumer Redress Obtained from Lender')
          )

          if (isFeesTable) {
            // Custom rendering for the fees table
            elements.push(
              <div key={currentIndex++} className="mb-6 overflow-x-auto">
                <table className="min-w-full border-collapse rounded-lg border border-gray-300 shadow-sm">
                  <thead>
                    {/* First header row */}
                    <tr style={{ backgroundColor: '#095678' }}>
                      <th
                        className="border border-gray-300 px-2 py-1 text-center text-xs font-semibold text-white"
                        rowSpan="2"
                      >
                        Band
                      </th>
                      <th
                        className="border border-gray-300 px-2 py-1 text-center text-xs font-semibold text-white"
                        colSpan="2"
                      >
                        Consumer Redress Obtained from Lender
                      </th>
                      <th
                        className="border border-gray-300 px-2 py-1 text-center text-xs font-semibold text-white"
                        rowSpan="2"
                      >
                        What MLM will charge you (inclusive of VAT)
                      </th>
                      <th
                        className="border border-gray-300 px-2 py-1 text-center text-xs font-semibold text-white"
                        rowSpan="2"
                      >
                        Maximum Total Fee Payable (inclusive of VAT)
                      </th>
                    </tr>
                    {/* Second header row */}
                    <tr style={{ backgroundColor: '#095678' }}>
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs font-semibold text-white">
                        Lower (£)
                      </th>
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs font-semibold text-white">
                        Upper (£)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        1
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £1
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £1,499
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        36%
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £504
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        2
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £1,500
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £9,999
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        33.6%
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £3,000
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        3
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £10,000
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £24,999
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        30%
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £6,000
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        4
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £25,000
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £49,999
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        24%
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £9,000
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        5
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £50,000
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        No limit
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        18%
                      </td>
                      <td className="font-raleway-numbers border border-gray-300 px-2 py-1 text-center text-xs font-medium text-gray-800">
                        £12,000
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )
          } else {
            // Standard table rendering for other tables
            const headerRow = tableRows[0]
              .split('|')
              .map(cell => cell.trim())
              .filter(cell => cell !== '')
            const dataRows = tableRows.slice(2).map(row =>
              row
                .split('|')
                .map(cell => cell.trim())
                .filter(cell => cell !== '')
            )

            elements.push(
              <div key={currentIndex++} className="mb-6 overflow-x-auto">
                <table className="min-w-full rounded-lg border border-gray-300 shadow-sm">
                  <thead className="bg-gray-600">
                    <tr>
                      {headerRow.map((header, idx) => (
                        <th
                          key={idx}
                          className="border-r border-gray-500 px-4 py-3 text-left font-semibold text-white last:border-r-0"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dataRows.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className={`border-b border-gray-300 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="border-r border-gray-300 px-4 py-3 font-medium text-gray-800 last:border-r-0"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
          i = j - 1 // Skip processed table rows
        }
      }
      // Handle lists
      else if (line.startsWith('- ')) {
        const listItems = []
        let j = i
        while (j < lines.length && lines[j].startsWith('- ')) {
          listItems.push(lines[j].replace('- ', ''))
          j++
        }

        elements.push(
          <ul
            key={currentIndex++}
            className="text-foreground mb-4 ml-0 list-disc space-y-2 pl-6"
          >
            {listItems.map((item, idx) => {
              // Process numbers first (before any HTML is introduced)
              let processedItem = item.replace(
                /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
                '<span class="font-raleway-numbers">$1</span>'
              )
              // Then process bold text
              processedItem = processedItem.replace(
                /\*\*(.*?)\*\*/g,
                '<strong class="font-semibold text-foreground">$1</strong>'
              )
              // Then process markdown links (mailto first to avoid double-matching)
              processedItem = processedItem.replace(
                /\[([^\]]+)\]\((mailto:[^)]+)\)/g,
                '<a href="$2" class="text-primary underline hover:opacity-80">$1</a>'
              )
              processedItem = processedItem.replace(
                /\[([^\]]+)\]\(([^)]+)\)/g,
                '<a href="$2" class="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>'
              )
              return (
                <li
                  key={idx}
                  className="pl-2"
                  dangerouslySetInnerHTML={{ __html: processedItem }}
                ></li>
              )
            })}
          </ul>
        )
        i = j - 1 // Skip processed list items
      }
      // Handle numbered lists
      else if (/^\d+\. /.test(line)) {
        const listItems = []
        let j = i
        while (j < lines.length && /^\d+\. /.test(lines[j])) {
          let item = lines[j].replace(/^\d+\. /, '')
          item = item.replace(
            /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
            '<span class="font-raleway-numbers">$1</span>'
          )
          item = item.replace(
            /\*\*(.*?)\*\*/g,
            '<strong class="font-semibold text-foreground">$1</strong>'
          )
          item = item.replace(
            /\[([^\]]+)\]\((mailto:[^)]+)\)/g,
            '<a href="$2" class="text-primary underline hover:opacity-80">$1</a>'
          )
          item = item.replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" class="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>'
          )
          listItems.push(item)
          j++
        }
        elements.push(
          <ol
            key={currentIndex++}
            className="text-foreground mb-4 ml-0 list-decimal space-y-2 pl-6"
          >
            {listItems.map((item, idx) => (
              <li
                key={idx}
                className="pl-2"
                dangerouslySetInnerHTML={{ __html: item }}
              ></li>
            ))}
          </ol>
        )
        i = j - 1
      }
      // Handle horizontal rules
      else if (line.trim() === '---') {
        elements.push(
          <hr key={currentIndex++} className="border-border my-8" />
        )
      }
      // Handle regular paragraphs
      else {
        // Check if line starts with spaces (indented paragraph)
        const isIndented = line.match(/^\s{2,}/)
        const trimmedLine = line.trim()

        // Process numbers first (before any HTML is introduced)
        let processedLine = trimmedLine.replace(
          /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
          '<span class="font-raleway-numbers">$1</span>'
        )
        // Then process bold text
        processedLine = processedLine.replace(
          /\*\*(.*?)\*\*/g,
          '<strong class="font-semibold text-foreground">$1</strong>'
        )
        // Then process markdown links (mailto first to avoid double-matching)
        processedLine = processedLine.replace(
          /\[([^\]]+)\]\((mailto:[^)]+)\)/g,
          '<a href="$2" class="text-primary underline hover:opacity-80">$1</a>'
        )
        processedLine = processedLine.replace(
          /\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" class="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>'
        )
        // Process bare URLs (not already inside an href)
        processedLine = processedLine.replace(
          /(?<!href="|">)(https?:\/\/[^\s<>"]+)/g,
          '<a href="$1" class="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>'
        )
        // Process bare www. URLs
        processedLine = processedLine.replace(
          /(?<!href="|">|\/\/)(www\.[^\s<>"]+)/g,
          '<a href="https://$1" class="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>'
        )

        elements.push(
          <p
            key={currentIndex++}
            className={`text-foreground mb-4 leading-relaxed ${isIndented ? 'pl-8' : ''}`}
            dangerouslySetInnerHTML={{ __html: processedLine }}
          ></p>
        )
      }
    }

    return elements
  }

  if (loading) {
    return (
      <div className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="animate-pulse">
            <div className="bg-muted mb-6 h-8 w-1/3 rounded"></div>
            <div className="space-y-4">
              <div className="bg-muted h-4 rounded"></div>
              <div className="bg-muted h-4 w-5/6 rounded"></div>
              <div className="bg-muted h-4 w-4/6 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8 text-center">
          <h1 className="text-foreground mb-4 text-3xl font-bold">
            Page Not Found
          </h1>
          <p className="text-muted-foreground mb-8">{error}</p>
        </div>
      </div>
    )
  }

  const config = pageConfig[page]

  return (
    <div className="flex-1">
      <SEO
        title={`${config?.title || 'Legal Document'} | Your Claim Matters`}
        description={`Read our ${config?.title?.toLowerCase() || 'legal document'} for Your Claim Matters car finance claims services. Important information about our terms, policies, and procedures.`}
        keywords={`Your Claim Matters ${config?.title?.toLowerCase() || 'legal document'}, car finance claims ${config?.title?.toLowerCase() || 'terms'}, legal information`}
        url={`/${page}`}
        image="/Image/Car-photo-illustration-claims1.webp"
        imageAlt={`Your Claim Matters ${config?.title || 'legal document'}`}
        schemaType="WebPage"
        schemaData={{
          mainEntity: {
            '@type': 'WebPage',
            name: config?.title || 'Legal Document',
            description: `${config?.title || 'Legal document'} for Your Claim Matters car finance claims services`,
            publisher: {
              '@type': 'Organization',
              name: 'Your Claim Matters',
              url: 'https://yourclaimmatters.co.uk',
            },
          },
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: config?.title || 'Legal Document', url: `/${page}` },
        ]}
      />
      {config?.hasHero && (
        <section className="relative flex min-h-[80vh] items-center justify-center bg-[url('/Image/Car-photo-illustration-claims1.webp')] bg-cover bg-[35%_center] bg-no-repeat md:min-h-[82vh] md:bg-[center_bottom_20%]">
          <div className="absolute inset-0 bg-black/60"></div>
          <div className="relative z-10 container mx-auto px-4 py-6 text-center md:py-8">
            <h1 className="mb-3 text-3xl leading-tight font-black tracking-tight text-white uppercase sm:text-4xl md:mb-4 md:text-6xl">
              {config.heroTitle}
            </h1>
            <p className="mx-auto mb-6 max-w-xl text-base leading-relaxed text-white sm:text-lg md:mb-8 md:max-w-3xl md:text-xl">
              {config.heroSubtitle}
            </p>
            <CTAButton className="bg-primary hover:bg-primary/90 text-primary-foreground" />
          </div>
        </section>
      )}

      <div className="container mx-auto max-w-6xl px-4 py-8">
        {!config?.hasHero && (
          <div className="mb-8">
            <h1 className="text-foreground mb-2 text-4xl font-bold">
              {config?.title || 'Legal Document'}
            </h1>
            <div className="bg-primary h-1 w-20 rounded"></div>
          </div>
        )}

        <div className="prose prose-lg max-w-none">
          {renderMarkdown(content)}

          {/* Add download button for terms-and-conditions page */}
          {page === 'terms-and-conditions' && (
            <div className="border-border mt-8 border-t pt-6">
              <div className="flex justify-center">
                <a
                  href="/content/terms-and-conditions.pdf"
                  download="YCM-Terms-and-Conditions.pdf"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold shadow-md transition-colors duration-200 hover:shadow-lg"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download PDF Version
                </a>
              </div>
              <p className="text-muted-foreground mt-2 text-center text-sm">
                Download the complete Terms and Conditions as a PDF document
              </p>
            </div>
          )}

          {/* Add download button for mlm-information-and-key-facts page */}
          {page === 'mlm-information-and-key-facts' && (
            <div className="border-border mt-8 border-t pt-6">
              <div className="flex justify-center">
                <a
                  href="/content/mlm-key-facts.pdf"
                  download="YCM-MLM-Key-Facts.pdf"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold shadow-md transition-colors duration-200 hover:shadow-lg"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download PDF Version
                </a>
              </div>
              <p className="text-muted-foreground mt-2 text-center text-sm">
                Download the complete Information & Key Facts as a PDF document
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MarkdownPage
